import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Preferences } from '@capacitor/preferences';
import { AuthBrowser } from 'capacitor-auth-browser';

const LOGIN_PKCE_VERIFIER_KEY = 'login_auth_verifier';
const LOGIN_PKCE_STATE_KEY = 'login_auth_state';

let cachedVerifier = '';
let cachedState = '';

/**
 * Extract the URL scheme from a native redirect URI (e.g. globaltasks:// -> globaltasks).
 * @param {string} redirectUri
 * @returns {string}
 */
export function getCallbackScheme(redirectUri) {
    const match = redirectUri.match(/^([a-zA-Z][a-zA-Z0-9+.-]*):/);
    if (!match) {
        throw new Error(`Invalid redirect URI: ${redirectUri}`);
    }
    return match[1];
}

const GOOGLE_IOS_CLIENT_ID_SUFFIX = '.apps.googleusercontent.com';

/**
 * Build the iOS Google OAuth redirect URI from a reversed client ID scheme.
 * @param {string} [clientId]
 * @param {string} [fallbackRedirectUri]
 * @returns {string|undefined}
 */
export function getGoogleIosOAuthRedirectUri(clientId, fallbackRedirectUri) {
    if (clientId?.endsWith(GOOGLE_IOS_CLIENT_ID_SUFFIX)) {
        const idPart = clientId.slice(0, -GOOGLE_IOS_CLIENT_ID_SUFFIX.length);
        return `com.googleusercontent.apps.${idPart}:/oauth2redirect`;
    }
    return fallbackRedirectUri;
}

/**
 * Open OAuth in ASWebAuthenticationSession on iOS, or Capacitor Browser on Android.
 * @param {string} authUrl
 * @param {string} redirectUri
 * @param {{ onBrowserFinished?: () => void | Promise<void> }} [options]
 * @returns {Promise<string|null>} Callback URL on iOS; null on Android (handled via appUrlOpen).
 */
export async function openNativeAuthSession(authUrl, redirectUri, { onBrowserFinished } = {}) {
    if (Capacitor.getPlatform() === 'ios') {
        const callbackScheme = getCallbackScheme(redirectUri);
        console.log('[NATIVE AUTH] Starting ASWebAuthenticationSession', { callbackScheme });
        const { url } = await AuthBrowser.authenticate({ url: authUrl, callbackScheme });
        console.log('[NATIVE AUTH] ASWebAuthenticationSession callback received');
        return url;
    }

    await Browser.removeAllListeners();
    await Browser.open({ url: authUrl });
    if (onBrowserFinished) {
        await Browser.addListener('browserFinished', onBrowserFinished);
    }
    return null;
}

export function getLoginPkceState() {
    return { verifier: cachedVerifier, state: cachedState };
}

export async function persistLoginPkceState(verifier, state) {
    cachedVerifier = verifier;
    cachedState = state;
    await Preferences.set({ key: LOGIN_PKCE_VERIFIER_KEY, value: verifier });
    await Preferences.set({ key: LOGIN_PKCE_STATE_KEY, value: state });
}

export function isValidLoginPkceCallback({ returnedCode, returnedState, verifier, state }) {
    return Boolean(returnedCode && verifier && state && returnedState && returnedState === state);
}

export async function restoreLoginPkceState() {
    if (!cachedVerifier) {
        const { value: verifier } = await Preferences.get({ key: LOGIN_PKCE_VERIFIER_KEY });
        if (verifier) {
            cachedVerifier = verifier;
        }
    }
    if (!cachedState) {
        const { value: state } = await Preferences.get({ key: LOGIN_PKCE_STATE_KEY });
        if (state) {
            cachedState = state;
        }
    }
    return getLoginPkceState();
}

export async function clearLoginPkceState() {
    cachedVerifier = '';
    cachedState = '';
    await Promise.allSettled([
        Preferences.remove({ key: LOGIN_PKCE_VERIFIER_KEY }),
        Preferences.remove({ key: LOGIN_PKCE_STATE_KEY }),
    ]);
}

export { LOGIN_PKCE_VERIFIER_KEY, LOGIN_PKCE_STATE_KEY };
